import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {
	longevityCategories, readResults, matchScoreRange, matchCategoryBlock, configurationIssues,
	type ParameterMapping, type ScoreRange, type CategoryRating,
} from './longevity-score-model'

export class LongevityScoreHero extends UcoastEl {
	static htmlSelector = 'longevity-score-hero'
	private refresh = () => this.renderResults()

	override connectedCallback() {
		super.connectedCallback()
		this.renderResults()
		window.addEventListener('popstate', this.refresh)
	}

	override disconnectedCallback() {
		window.removeEventListener('popstate', this.refresh)
		super.disconnectedCallback()
	}

	private renderResults() {
		const editor = this.dataset.ucDesignMode === 'true'
		const mapping = {
			total: this.dataset.ucParameterTotal ?? '',
			product: this.dataset.ucParameterProduct ?? '',
			...Object.fromEntries(longevityCategories.map((category) => {
				const card = q.rs<HTMLElement>(`[data-uc-category-card="${category}"]`, this)
				return [category, { parameter: card.dataset.ucParameter ?? '', category: card.dataset.ucTierCategory ?? '' }]
			})),
		} as ParameterMapping
		const results = readResults(new URLSearchParams(window.location.search), mapping)
		// A recommendation label is text metadata, not a Shopify handle/ID or a CTA URL.
		if (results.product === null) delete this.dataset.ucRecommendedProduct
		else this.dataset.ucRecommendedProduct = results.product
		if (editor) {
			results.total ??= Number(this.dataset.ucPreviewScore)
			longevityCategories.forEach((category) => {
				const preview = q.rs<HTMLElement>(`[data-uc-category-card="${category}"]`, this).dataset.ucPreviewRating
				const rating = preview?.trim() ? Number(preview) : NaN
				if (Number.isInteger(rating) && rating >= 0 && rating <= 5) results.ratings[category] ??= rating
			})
		}

		const scoreDescriptions = Array.from(q.rl<HTMLElement>('[data-uc-score-description]', this))
		const ranges = scoreDescriptions.filter((element) => element.querySelector('[data-uc-description-copy]')?.textContent?.trim()).map((element) => ({
			min: Number(element.dataset.ucRangeMin), max: Number(element.dataset.ucRangeMax), element,
		})) satisfies (ScoreRange & { element: HTMLElement })[]
		const categoryDescriptions = Array.from(q.ol<HTMLElement>('[data-uc-category-description]', this) ?? [])
		const blocks = categoryDescriptions.map((element) => ({
			category: element.dataset.ucCategory || '', rating: Number(element.dataset.ucRating), element,
		})) satisfies (CategoryRating & { element: HTMLElement })[]
		const matchedRange = matchScoreRange(results.total, ranges)
		q.rs('[data-uc-score-value]', this).textContent = results.total === null ? '—' : String(results.total)
		q.rs('[data-uc-score-accessible]', this).textContent = results.total === null ? 'Score unavailable' : `${results.total} out of 100`
		q.rs<HTMLElement>('[data-uc-results-unavailable]', this).hidden = results.total !== null
		this.dataset.ucScoreState = results.total === null ? 'unavailable' : matchedRange ? 'matched' : 'unmatched'
		scoreDescriptions.forEach((element) => {
			const matched = matchedRange?.element === element
			element.hidden = !editor && !matched
			element.dataset.ucMatched = String(matched)
		})

		longevityCategories.forEach((category) => {
			const card = q.rs<HTMLElement>(`[data-uc-category-card="${category}"]`, this)
			const rating = results.ratings[category]
			const matchedBlock = matchCategoryBlock(category, rating, blocks)
			card.dataset.ucRatingState = rating === null ? 'unavailable' : matchedBlock ? 'matched' : 'unmatched'
			q.rs('[data-uc-rating-accessible]', card).textContent = rating === null ? 'Rating unavailable' : `${rating} out of 5 stars`
			q.rs('[data-uc-rating-value]', card).textContent = rating === null ? 'Rating unavailable' : `${rating}/5`
			q.rl<HTMLElement>('[data-uc-star]', card).forEach((star) => {
				star.dataset.ucFilled = String(rating !== null && Number(star.dataset.ucStar) <= rating)
			})
			blocks.filter((block) => block.category === category).forEach((block) => {
				block.element.hidden = !editor && block !== matchedBlock
				block.element.dataset.ucMatched = String(block === matchedBlock)
			})
		})

		if (editor) {
			const issues = configurationIssues(ranges, blocks)
			if (!matchedRange) issues.push('The preview score has no configured description. Fill the rich text and set the intended ranges.')
			longevityCategories.forEach((category) => {
				if (results.ratings[category] !== null && !matchCategoryBlock(category, results.ratings[category], blocks)) issues.push(`The preview ${category} rating has no matching description.`)
			})
			const warning = q.rs<HTMLElement>('[data-uc-editor-warning]', this)
			warning.textContent = issues.join(' ')
			warning.hidden = issues.length === 0
		}
	}
}
